import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { MdCancel } from "react-icons/md";

const GooglePlaceFilter = ({
  onAddressChange,
  onNearbyResults = () => {},
  inputStyle = {},
  placeholder = "Enter a place...",
  apiKey = process.env.REACT_APP_GOOGLE_API_KEY,
  countryRestrictions = [],
}) => {
  const inputRef = useRef(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const autocompleteRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (window.google?.maps?.places) {
      setScriptLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    script.onerror = () => {
      setError("Failed to load Google Maps API");
    };

    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        document.head.removeChild(script);
      }
    };
  }, [apiKey]);

  useEffect(() => {
    if (!scriptLoaded || !inputRef.current) return;

    const options = {
      // Add more fields to get richer data
      fields: [
        "address_components",
        "formatted_address",
        "name",
        "geometry",
        "photos",
        "place_id",
        "icon",
        "types",
        "url",
      ],
    };

    if (countryRestrictions.length > 0) {
      options.componentRestrictions = { country: countryRestrictions };
    }

    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      inputRef.current,
      options
    );

    const handlePlaceChange = () => {
      setLoading(true);
      setError(null);

      const place = autocompleteRef.current.getPlace();
      if (!place?.address_components) {
        setLoading(false);
        return;
      }

      const addressComponents = {};
      place.address_components.forEach((component) => {
        component.types.forEach((type) => {
          addressComponents[type] = component.long_name;
        });
      });

      const city =
        addressComponents.locality ||
        addressComponents.administrative_area_level_2 ||
        "";
      const state = addressComponents.administrative_area_level_1 || "";
      const country = addressComponents.country || "";
      const formattedAddress = place.formatted_address || "";

      const photos =
        place.photos?.map((p) =>
          p.getUrl({ maxWidth: 500, maxHeight: 300 })
        ) || [];

      const coordinates = {
        lat: place.geometry?.location?.lat(),
        lng: place.geometry?.location?.lng(),
      };

      // Update input
      setInputValue(formattedAddress);

      // Send data to parent
      onAddressChange({
        city,
        state,
        country,
        formattedAddress,
        coordinates,
        placeId: place.place_id,
        photos,
        name: place.name,
        icon: place.icon,
        types: place.types,
        url: place.url,
      });

      // Trigger nearby search using PlacesService
      if (coordinates.lat && coordinates.lng) {
        const service = new window.google.maps.places.PlacesService(
          document.createElement("div")
        );

        const request = {
          location: new window.google.maps.LatLng(
            coordinates.lat,
            coordinates.lng
          ),
          radius: 1000, // meters
          type: ["restaurant", "store", "point_of_interest"],
        };

        service.nearbySearch(request, (results, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            results.length > 0
          ) {
            onNearbyResults(results);
          } else {
            onNearbyResults([]);
          }
        });
      }

      setLoading(false);
    };

    autocompleteRef.current.addListener("place_changed", handlePlaceChange);

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(
          autocompleteRef.current
        );
      }
    };
  }, [scriptLoaded, countryRestrictions, onAddressChange, onNearbyResults]);

  const handleClear = () => {
    setInputValue("");
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    onAddressChange({
      city: "",
      state: "",
      country: "",
      formattedAddress: "",
      coordinates: null,
    });
    onNearbyResults([]);
  };

  return (
    <div
      className="anvCustom_floatInput form-floating address-autocomplete"
      style={{ position: "relative" }}
    >
      <input
        className="form-control"
        ref={inputRef}
        placeholder={placeholder}
        style={{ ...inputStyle, paddingRight: "35px" }}
        disabled={loading}
        required
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
      />

      {inputValue && (
        <button
          type="button"
          onClick={handleClear}
          style={{
            position: "absolute",
            right: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
            color: "#666",
            zIndex: 2,
          }}
        >
          <MdCancel size={20} />
        </button>
      )}

      {loading && <div className="loading-indicator">Loading details...</div>}
      {error && (
        <div className="error-message" style={{ color: "#ff4444" }}>
          {error}
        </div>
      )}
    </div>
  );
};

GooglePlaceFilter.propTypes = {
  onAddressChange: PropTypes.func.isRequired,
  onNearbyResults: PropTypes.func,
  inputStyle: PropTypes.object,
  placeholder: PropTypes.string,
  apiKey: PropTypes.string,
  countryRestrictions: PropTypes.arrayOf(PropTypes.string),
};

GooglePlaceFilter.defaultProps = {
  inputStyle: {},
  placeholder: "Enter a place...",
  countryRestrictions: [],
  onNearbyResults: () => {},
};

export default GooglePlaceFilter;

